import { Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  Gift,
  Users,
  Settings,
  ArrowLeftRight,
  CreditCard,
  BarChart3,
  Receipt,
  UserRoundPlus,
  ClipboardList,
  Package,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from './ui/sidebar';
import { NavDocuments } from './NavDocuments';
import styles from './AppSidebar.module.scss';
import { useTranslation } from 'react-i18next';

interface BaseMenuItem {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface RegularMenuItem extends BaseMenuItem {
  type: 'item';
  url: string;
}

interface CollapsibleMenuItem extends BaseMenuItem {
  type: 'group';
  items: {
    title: string;
    url: string;
    icon: React.ComponentType<{ className?: string }>;
  }[];
}

type MenuItem = RegularMenuItem | CollapsibleMenuItem;

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {}

export function AppSidebar({ ...props }: AppSidebarProps) {
  const { state } = useSidebar();
  const { t } = useTranslation();

  // Admin menu items - có thêm page cho admin thì thêm nav ở đây
  const adminMenuItems: MenuItem[] = [
    {
      type: 'item',
      title: t('sidebar.dashboard'),
      url: '/admin/dashboard',
      icon: LayoutDashboard,
    },
    {
      type: 'item',
      title: t('sidebar.investors'),
      url: '/admin/investors',
      icon: UserRoundPlus,
    },
    {
      type: 'item',
      title: t('sidebar.businessDirectors'),
      url: '/admin/gdkd',
      icon: Briefcase,
    },
    {
      type: 'item',
      title: t('sidebar.plans'),
      url: '/admin/plans',
      icon: Package,
    },
    {
      type: 'item',
      title: t('sidebar.transactions'),
      url: '/admin/transactions',
      icon: Receipt,
    },
    {
      type: 'item',
      title: t('sidebar.investmentTransactions'),
      url: '/admin/investment',
      icon: CreditCard,
    },
    {
      type: 'item',
      title: t('sidebar.commissions'),
      url: '/admin/commissions',
      icon: Gift,
    },
    {
      type: 'item',
      title: t('sidebar.interestPaymentManagement'),
      url: '/admin/interest-payments',
      icon: BarChart3,
    },
    {
      type: 'item',
      title: t('sidebar.batchHistory'),
      url: '/admin/batch-history',
      icon: ClipboardList,
    },
    {
      type: 'item',
      title: t('sidebar.transferHistory'),
      url: '/admin/transfer-history',
      icon: ArrowLeftRight,
    },
    {
      type: 'group',
      title: t('sidebar.users'),
      icon: Users,
      items: [
        {
          title: t('sidebar.userManagement'),
          url: '/admin/users',
          icon: Users,
        },
        {
          title: t('sidebar.createBatchAccounts'),
          url: '/admin/users/create-batch',
          icon: Users,
        }
      ],
    },
    {
      type: 'group',
      title: t('sidebar.settings'),
      icon: Settings,
      items: [
        {
          title: t('sidebar.revenueManagement'),
          url: '/admin/settings/revenue',
          icon: CreditCard,
        },
        {
          title: t('sidebar.settleDaily'),
          url: '/admin/settings/settle-daily',
          icon: BarChart3,
        }
      ],
    },
  ];

  return (
    <Sidebar
      collapsible="icon"
      className={cn(
        styles.appSidebar,
        state === 'collapsed' ? styles.collapsed : styles.expanded
      )}
      {...props}
    >
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <Link to="/admin/dashboard" className={styles.headerLink}>
              <div className={styles.headerContent}>
                <div className="w-10 h-10 flex items-start justify-start">
                  <img src="/images/logo/logo.png" alt="logo BeQ Holdings" className='w-8 md:w-10 h-8 md:h-10'/>
                </div>
                {state === 'expanded' && (
                  <div className={styles.headerText}>
                    <span className={styles.brandName}>{t('common.beqRewards')}</span>
                  </div>
                )}
              </div>
            </Link>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <NavDocuments items={adminMenuItems} />
      </SidebarContent>
    </Sidebar>
  );
}
